/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) DataTable tools standardidir.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalici degisiklik yapilmaz.
 * Degisiklik gerekiyorsa Core'da guncellenir ve senkron ile diger repolara yayilir.
 *
 * DataTableTools, tablo islemlerini DataTable + SmartFilter standardindan uretir.
 */

import React, { createContext, useCallback, useContext, useMemo } from "react";
import type { QubeProDataTableIdentity } from "../../config/app.config";
import { usePermission } from "../../hooks/usePermission";
import { DEFAULT_LANGUAGE, type SupportedLanguage } from "../../utils/language.utils";
import {
  createEmptySmartFilter,
  SmartFilter,
  type SmartFilterField,
  type SmartFilterValue,
} from "./SmartFilter";
import "./DataTable.css";
import "./DataTableTools.css";

type DataTableToolsStatus = "all" | "active" | "inactive";

const DATA_TABLE_TOOL_LABELS = {
  tr: {
    noPermission: "Yetkiniz yok",
    status: "Durum",
    active: "Aktif",
    inactive: "Pasif",
  },
  en: {
    noPermission: "No permission",
    status: "Status",
    active: "Active",
    inactive: "Inactive",
  },
  ar: {
    noPermission: "ليس لديك صلاحية",
    status: "الحالة",
    active: "نشط",
    inactive: "غير نشط",
  },
};

function getDataTableToolLabels(language: SupportedLanguage) {
  if (language === "tr") return DATA_TABLE_TOOL_LABELS.tr;
  if (language === "ar") return DATA_TABLE_TOOL_LABELS.ar;
  return DATA_TABLE_TOOL_LABELS.en;
}

interface DataTableToolsContextValue {
  identity?: QubeProDataTableIdentity;
  language?: SupportedLanguage;
}

const DataTableToolsContext = createContext<DataTableToolsContextValue>({});

export function DataTableToolsProvider({
  value,
  children,
}: {
  value: DataTableToolsContextValue;
  children: React.ReactNode;
}) {
  return (
    <DataTableToolsContext.Provider value={value}>
      {children}
    </DataTableToolsContext.Provider>
  );
}

interface DataTableToolsProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  /** Verilirse genel `common.searchPlaceholder` yerine kullanilir */
  searchPlaceholder?: string;
  selectedStatus: DataTableToolsStatus;
  onStatusChange: (status: DataTableToolsStatus) => void;
  onClearTools: () => void;
  onAddClick?: () => void;
  addPermission?: string;
  disableClear?: boolean;
  disableAdd?: boolean;
  addDisabledTitle?: string;
  addButtonClassName?: string;
  addButtonTitle?: string;
  /** Guided Learning semantic target for the create/add action. */
  addLearningTargetId?: string;
  language?: SupportedLanguage;
  /** Durum select yerine ozel arac alani (orn. Dil + Tablo). Verilirse status select gosterilmez. */
  customTools?: React.ReactNode;
  /** Sayfa kendi durum/tip filtresini tablo ustunde veriyorsa varsayilan aktif/pasif SmartFilter alanini gizler. */
  hideStatusFilter?: boolean;
  /** true ise sagdaki "yeni kayit" butonu gosterilmez (salt okunur listeler). */
  hideAdd?: boolean;
  /** true ise arama alani gosterilmez; sayfaya ozel filtrelerin tools satirinda kalmasini saglar. */
  hideSearch?: boolean;
  /** Geriye uyumluluk icin korunur; yeni adaptorde ekstra wrapper uretmez. */
  embedded?: boolean;
}

function pathFallbackIdentity(): QubeProDataTableIdentity {
  const path =
    typeof window === "undefined"
      ? "data-table-tools"
      : window.location.pathname || "data-table-tools";
  const page = path
    .replace(/^\/+/, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "data-table-tools";

  return { module: "dataTableTools", page, component: "main" };
}

export const DataTableTools: React.FC<DataTableToolsProps> = ({
  searchTerm,
  onSearchChange,
  selectedStatus,
  onStatusChange,
  onClearTools,
  onAddClick,
  addPermission,
  disableClear = false,
  disableAdd = false,
  addDisabledTitle,
  addButtonClassName,
  addButtonTitle,
  addLearningTargetId,
  language,
  customTools,
  hideStatusFilter = false,
  hideAdd = false,
  hideSearch = false,
  embedded = false,
}) => {
  const context = useContext(DataTableToolsContext);
  const { hasPermission } = usePermission();
  const resolvedLanguage = language ?? context.language ?? DEFAULT_LANGUAGE;
  const labels = getDataTableToolLabels(resolvedLanguage);
  const identity = context.identity ?? pathFallbackIdentity();
  const canAddByPermission = addPermission ? hasPermission(addPermission) : true;
  const isAddDisabled = disableAdd || !canAddByPermission;
  const disabledTitle = addDisabledTitle || labels.noPermission;

  const fields = useMemo<SmartFilterField[]>(
    () => hideStatusFilter ? [] : [
      {
        key: "status",
        label: labels.status,
        type: "select",
        options: [
          { label: labels.active, value: "active" },
          { label: labels.inactive, value: "inactive" },
        ],
      },
    ],
    [hideStatusFilter, labels],
  );

  const value = useMemo<SmartFilterValue>(() => {
    const next = createEmptySmartFilter();
    if (searchTerm.trim()) next.quickSearch = searchTerm;
    if (!customTools && !hideStatusFilter && selectedStatus !== "all") {
      next.rules = [{ field: "status", operator: "equals", value: selectedStatus }];
    }
    return next;
  }, [customTools, hideStatusFilter, searchTerm, selectedStatus]);

  const handleApply = useCallback(
    (next: SmartFilterValue) => {
      onSearchChange(next.quickSearch?.trim() || "");
      if (!customTools && !hideStatusFilter) {
        const statusRule = next.rules.find((rule) => rule.field === "status");
        const status = String(statusRule?.value || "all");
        onStatusChange(status === "active" || status === "inactive" ? status : "all");
      }
    },
    [customTools, hideStatusFilter, onSearchChange, onStatusChange],
  );

  const node = (
    <>
      <SmartFilter
        identity={identity}
        fields={fields}
        value={value}
        onApply={handleApply}
        onClear={onClearTools}
        onAddClick={hideAdd ? undefined : onAddClick}
        addDisabled={isAddDisabled}
        addTitle={isAddDisabled ? disabledTitle : addButtonTitle}
        addButtonClassName={addButtonClassName}
        addLearningTargetId={addLearningTargetId}
        hasExternalFilters={!disableClear}
        hideQuickSearch={hideSearch}
        language={resolvedLanguage}
      />
      {customTools ? (
        <div className="datatable-tools-custom-slot" data-no-row-click="true">
          {customTools}
        </div>
      ) : null}
    </>
  );

  if (embedded) return node;

  return (
    <div className="datatable-toolbar" data-no-row-click="true">
      <div className="datatable-toolbar-left">{node}</div>
    </div>
  );
};
